/**
 * Datum wire contract: the single source of truth for every API payload.
 * `apps/api` validates through these via @hono/zod-openapi; `apps/web` reuses them
 * for form validation and hc<> client typings. Field names are snake_case on the
 * wire (matching D1 columns); TypeScript identifiers stay camelCase internally.
 */
import { z } from 'zod'

/** Re-exported so apps validate against the identical zod instance as the wire contract. */
export { z }

// ---------------------------------------------------------------------------
// Primitives
// ---------------------------------------------------------------------------

export const uuidSchema = z.uuid()
export const timestampMsSchema = z.number().int()

export const coordinateSchema = z.object({
  latitude: z.number().min(-90).max(90),
  longitude: z.number().min(-180).max(180),
  altitude: z.number().nullable().optional(),
  accuracy: z.number().min(0).nullable().optional(),
})
export type Coordinate = z.infer<typeof coordinateSchema>

export const bboxQuerySchema = z.object({
  min_lat: z.coerce.number().min(-90).max(90),
  max_lat: z.coerce.number().min(-90).max(90),
  min_lng: z.coerce.number().min(-180).max(180),
  max_lng: z.coerce.number().min(-180).max(180),
})
export type BBoxQuery = z.infer<typeof bboxQuerySchema>

export const geoJsonPositionSchema = z.tuple([z.number(), z.number()])
export const geoJsonPolygonSchema = z.object({
  type: z.literal('Polygon'),
  coordinates: z.array(z.array(geoJsonPositionSchema)).min(1),
})
export const geoJsonMultiPolygonSchema = z.object({
  type: z.literal('MultiPolygon'),
  coordinates: z.array(z.array(z.array(geoJsonPositionSchema))).min(1),
})
export const waypointGeometrySchema = z.discriminatedUnion('type', [
  geoJsonPolygonSchema,
  geoJsonMultiPolygonSchema,
])
export type WaypointGeometry = z.infer<typeof waypointGeometrySchema>

// ---------------------------------------------------------------------------
// Note content (block AST mirrored by packages/shared/src/blocks.ts)
// ---------------------------------------------------------------------------

export const headingBlockSchema = z.object({
  type: z.literal('heading'),
  level: z.union([z.literal(1), z.literal(2), z.literal(3)]),
  text: z.string(),
})
export const paragraphBlockSchema = z.object({
  type: z.literal('paragraph'),
  text: z.string(),
})
export const todoBlockSchema = z.object({
  type: z.literal('todo'),
  text: z.string(),
  done: z.boolean(),
})
export const benchBlockSchema = z.object({
  type: z.literal('bench'),
  label: z.string(),
  value: z.string(),
  unit: z.string().optional(),
})
export const attachmentBlockSchema = z.object({
  type: z.literal('attachment'),
  kind: z.union([z.literal('photo'), z.literal('audio')]),
  attachmentId: z.string().optional(),
})
export const blockSchema = z.discriminatedUnion('type', [
  headingBlockSchema,
  paragraphBlockSchema,
  todoBlockSchema,
  benchBlockSchema,
  attachmentBlockSchema,
])
export const blocksSchema = z.array(blockSchema)
export type Block = z.infer<typeof blockSchema>

// ---------------------------------------------------------------------------
// Organizations (Better Auth plugin shape Datum reads for tenant scoping)
// ---------------------------------------------------------------------------

export const organizationSummarySchema = z.object({
  id: z.string(),
  name: z.string(),
  slug: z.string(),
})
export type OrganizationSummary = z.infer<typeof organizationSummarySchema>

// ---------------------------------------------------------------------------
// Datum points (notes)
// ---------------------------------------------------------------------------

export const pointDtoSchema = z.object({
  id: uuidSchema,
  org_id: uuidSchema,
  user_id: uuidSchema,
  title: z.string(),
  content_raw: z.string(),
  /** JSON.stringify(Block[]) — derived server-side from content_raw, never trusted from clients. */
  content_json: z.string(),
  latitude: z.number(),
  longitude: z.number(),
  altitude: z.number().nullable(),
  accuracy: z.number().nullable(),
  waypoint_id: uuidSchema.nullable(),
  summary: z.string().nullable(),
  tags: z.array(z.string()),
  version: z.number().int(),
  created_at: timestampMsSchema,
  updated_at: timestampMsSchema,
  deleted_at: timestampMsSchema.nullable(),
})
export type PointDto = z.infer<typeof pointDtoSchema>

export const pointCreateSchema = z.object({
  /** Client-generated so offline notes keep a stable identity until first sync. */
  id: uuidSchema,
  title: z.string().min(1).max(200),
  content_raw: z.string().max(200_000).default(''),
  latitude: z.number().min(-90).max(90),
  longitude: z.number().min(-180).max(180),
  altitude: z.number().nullable().optional(),
  accuracy: z.number().min(0).nullable().optional(),
  waypoint_id: uuidSchema.nullable().optional(),
})
export type PointCreate = z.infer<typeof pointCreateSchema>

export const pointUpdateSchema = z
  .object({
    title: z.string().min(1).max(200).optional(),
    content_raw: z.string().max(200_000).optional(),
    latitude: z.number().min(-90).max(90).optional(),
    longitude: z.number().min(-180).max(180).optional(),
    altitude: z.number().nullable().optional(),
    accuracy: z.number().min(0).nullable().optional(),
    waypoint_id: uuidSchema.nullable().optional(),
    deleted_at: timestampMsSchema.nullable().optional(),
    /** The version the client last read; the server rejects stale writes with 409. */
    base_version: z.number().int().min(1),
  })
  .refine((update) => Object.keys(update).some((key) => key !== 'base_version'), {
    message: 'At least one field must change alongside base_version',
  })
export type PointUpdate = z.infer<typeof pointUpdateSchema>

export const pointListQuerySchema = bboxQuerySchema.partial().extend({
  waypoint_id: uuidSchema.optional(),
  include_deleted: z.coerce.boolean().default(false),
  limit: z.coerce.number().int().min(1).max(200).default(50),
  cursor: z.string().optional(),
})
export type PointListQuery = z.infer<typeof pointListQuerySchema>

export const pointListResponseSchema = z.object({
  points: z.array(pointDtoSchema),
  next_cursor: z.string().nullable(),
})

/** 409 body: OCC rejection carrying the authoritative row for client-side adoption/merge. */
export const conflictPointResponseSchema = z.object({
  error: z.object({ code: z.string(), message: z.string() }),
  server: pointDtoSchema,
})
export type ConflictPointResponse = z.infer<typeof conflictPointResponseSchema>

// ---------------------------------------------------------------------------
// Waypoints
// ---------------------------------------------------------------------------

export const waypointDtoSchema = z.object({
  id: uuidSchema,
  org_id: uuidSchema,
  name: z.string(),
  building_code: z.string().nullable(),
  floor_level: z.string().nullable(),
  polygon_geojson: waypointGeometrySchema,
  created_at: timestampMsSchema,
  updated_at: timestampMsSchema,
})
export type WaypointDto = z.infer<typeof waypointDtoSchema>

export const waypointCreateSchema = z.object({
  id: uuidSchema,
  name: z.string().min(1).max(120),
  building_code: z.string().max(40).nullable().optional(),
  floor_level: z.string().max(40).nullable().optional(),
  polygon_geojson: waypointGeometrySchema,
})
export type WaypointCreate = z.infer<typeof waypointCreateSchema>

export const waypointUpdateSchema = waypointCreateSchema.omit({ id: true }).partial()

export const waypointListResponseSchema = z.object({
  waypoints: z.array(waypointDtoSchema),
})

// ---------------------------------------------------------------------------
// Attachments & media
// ---------------------------------------------------------------------------

export const attachmentDtoSchema = z.object({
  id: uuidSchema,
  datum_point_id: uuidSchema,
  org_id: uuidSchema,
  r2_key: z.string(),
  file_type: z.string(),
  file_size: z.number().int(),
  transcription: z.string().nullable(),
  duration_ms: z.number().int().nullable(),
  created_at: timestampMsSchema,
})
export type AttachmentDto = z.infer<typeof attachmentDtoSchema>

export const uploadRequestSchema = z.object({
  datum_point_id: uuidSchema,
  file_name: z.string().min(1).max(200),
  content_type: z.string().min(1),
  size_bytes: z.number().int().min(1),
})
export type UploadRequest = z.infer<typeof uploadRequestSchema>

export const uploadTicketSchema = z.object({
  upload_url: z.string(),
  r2_key: z.string(),
  attachment_id: uuidSchema,
  expires_at: timestampMsSchema,
})
export type UploadTicket = z.infer<typeof uploadTicketSchema>

/** Sent by the client once the presigned PUT completes. */
export const attachmentCommitSchema = z.object({
  attachment_id: uuidSchema,
})
export const attachmentCommitResultSchema = z.object({
  attachment: attachmentDtoSchema,
  /** Ready-to-insert token for the note editor's slash flow. */
  editor_token: z.string(),
})

export const attachmentListResponseSchema = z.object({
  attachments: z.array(attachmentDtoSchema),
})

/** Response of POST /api/media/attachments/:id/download-url. */
export const downloadUrlResponseSchema = z.object({
  download_url: z.string(),
  expires_at: timestampMsSchema,
})
export type DownloadUrlResponse = z.infer<typeof downloadUrlResponseSchema>

// ---------------------------------------------------------------------------
// Sync (optimistic concurrency, batch push + incremental pull)
// ---------------------------------------------------------------------------

export const syncChangeSchema = z.object({
  base_version: z.number().int().min(1),
  point: pointCreateSchema.extend({
    /** Tombstone marker; null/omitted means live. */
    deleted_at: timestampMsSchema.nullable().optional(),
  }),
})
export type SyncChange = z.infer<typeof syncChangeSchema>

export const syncPushRequestSchema = z.object({
  changes: z.array(syncChangeSchema).min(1).max(100),
})
export type SyncPushRequest = z.infer<typeof syncPushRequestSchema>

export const syncChangeResultSchema = z.discriminatedUnion('status', [
  z.object({
    id: uuidSchema,
    status: z.union([z.literal('applied'), z.literal('created')]),
    version: z.number().int(),
    updated_at: timestampMsSchema,
  }),
  z.object({
    id: uuidSchema,
    status: z.literal('conflict'),
    /** Authoritative server row; the client must adopt or merge from this. */
    server: pointDtoSchema,
  }),
])
export type SyncChangeResult = z.infer<typeof syncChangeResultSchema>

export const syncPushResponseSchema = z.object({
  results: z.array(syncChangeResultSchema),
})

export const syncPullQuerySchema = z.object({
  since: z.coerce.number().int().min(0),
  limit: z.coerce.number().int().min(1).max(200).default(50),
})
export const syncPullResponseSchema = z.object({
  points: z.array(pointDtoSchema),
  /** Pass as `since` on the next pull; null when no more pages at this instant. */
  next_since: timestampMsSchema.nullable(),
  server_time: timestampMsSchema,
})

// ---------------------------------------------------------------------------
// AI processing
// ---------------------------------------------------------------------------

export const processPointRequestSchema = z.object({
  datum_point_id: uuidSchema,
})
export const processPointResponseSchema = z.object({
  point: pointDtoSchema,
  transcribed_attachments: z.number().int(),
})

// ---------------------------------------------------------------------------
// Shared response wrappers
// ---------------------------------------------------------------------------

export const errorResponseSchema = z.object({
  error: z.object({
    code: z.string(),
    message: z.string(),
  }),
})
export type ErrorResponse = z.infer<typeof errorResponseSchema>

// ---------------------------------------------------------------------------
// Account export & deletion (W6). GET /api/export/me bundles everything the
// account can reach; DELETE /api/account/me wipes it. Not consumed by the
// Android client (account management is web/operator-only), so no Kotlin twin.
// ---------------------------------------------------------------------------

export const exportUserSchema = z.object({
  id: z.string(),
  name: z.string(),
  email: z.string(),
  created_at: timestampMsSchema,
})
export type ExportUser = z.infer<typeof exportUserSchema>

export const exportAttachmentSchema = attachmentDtoSchema.extend({
  /** Freshly signed download URL, valid until url_expires_at. */
  download_url: z.string(),
  url_expires_at: timestampMsSchema,
})
export type ExportAttachment = z.infer<typeof exportAttachmentSchema>

export const exportBundleSchema = z.object({
  exported_at: timestampMsSchema,
  user: exportUserSchema,
  organizations: z.array(organizationSummarySchema),
  waypoints: z.array(waypointDtoSchema),
  points: z.array(pointDtoSchema),
  attachments: z.array(exportAttachmentSchema),
})
export type ExportBundle = z.infer<typeof exportBundleSchema>

export const deleteAccountRequestSchema = z.object({
  password: z.string().min(1),
})
export type DeleteAccountRequest = z.infer<typeof deleteAccountRequestSchema>

export const deleteAccountResponseSchema = z.object({
  deleted: z.literal(true),
  organizations_removed: z.number().int(),
  media_objects_removed: z.number().int(),
})
export type DeleteAccountResponse = z.infer<typeof deleteAccountResponseSchema>

export const healthResponseSchema = z.object({
  status: z.literal('ok'),
  version: z.string(),
})
